/**
 * Structured rejection detail from an upstream AI provider (e.g. a video-gen
 * content-policy violation). All fields are best-effort and optional.
 *
 * Mirror of the canonical server type
 * (venus server/cloud-run/src/types/providerError.ts).
 */
export interface ProviderErrorDetail {
  /** Path to the offending request field, e.g. ["body", "image_urls"]. */
  loc?: (string | number)[]
  /** Provider error type, e.g. "content_policy_violation". */
  type?: string
  /** Provider-internal reason code, e.g. "partner_validation_failed". */
  reason?: string
  /** Provider's verbatim human-readable message. */
  msg?: string
}

/**
 * Base error for rundot API failures that include a machine-readable code.
 * Thrown by SDK methods when the server returns a structured error response.
 */
export class RundotApiError extends Error {
  readonly code: string
  /** HTTP status when the error came over HTTP; `0` for transport/RPC errors with no HTTP status. */
  readonly status: number
  /**
   * Optional human-readable cause from the server (e.g. an upstream provider's
   * validation message), surfaced alongside the controlled `message` so callers
   * can show the specific reason rather than a generic failure.
   *
   * This is a flattened STRING. For the machine-readable breakdown of a
   * provider rejection (type / reason / offending field), use `errorDetail`.
   */
  readonly detail?: string
  /**
   * Optional structured provider rejection detail (e.g.
   * `{ type: 'content_policy_violation', reason: 'partner_validation_failed' }`).
   * Unlike `detail` (a display string), this is meant for programmatic handling.
   */
  readonly errorDetail?: ProviderErrorDetail

  constructor(
    code: string,
    message: string,
    status = 0,
    detail?: string,
    errorDetail?: ProviderErrorDetail,
  ) {
    super(message)
    this.name = 'RundotApiError'
    this.code = code
    this.status = status
    this.detail = detail
    this.errorDetail = errorDetail
  }
}

/**
 * Thrown when a request is rate-limited (HTTP 429).
 * `retryAfterMs` indicates how long to wait before retrying.
 */
export class RateLimitedError extends RundotApiError {
  readonly retryAfterMs: number

  constructor(retryAfterMs: number, message?: string) {
    super(
      'RATE_LIMITED',
      message ?? `Rate limited. Retry after ${Math.ceil(retryAfterMs / 1000)}s.`,
      429,
    )
    this.name = 'RateLimitedError'
    this.retryAfterMs = retryAfterMs
  }
}
